/**
 * The one supplier link a discovery card shows. Research stores two: the
 * product page for this part (`productLink`) and the supplier's homepage
 * (`supplierWebsite`). The product page wins when there is one; the link is
 * labelled for what it really is, so "Product page" never opens a homepage.
 */

export type WebsiteKind = 'product' | 'supplier';

const urlTextOf = (value: unknown): string | undefined =>
  typeof value === 'string' && /^https?:\/\//i.test(value.trim()) ? value.trim() : undefined;

/** A bare domain ("https://acme.com/") is a homepage, whatever field it came in. */
const isHomepage = (url: string): boolean => {
  try {
    const { pathname, search } = new URL(url);
    return (pathname === '' || pathname === '/') && !search;
  } catch {
    return false;
  }
};

/** Snake_case and older aliases are research payloads from before the camelCase contract. */
export const websiteOf = (
  s: Record<string, unknown> | null | undefined,
): { websiteUrl?: string; websiteKind?: WebsiteKind } => {
  const product = [s?.productLink, s?.product_link, s?.productPageURL].map(urlTextOf).find(Boolean);
  const site = [s?.supplierWebsite, s?.supplier_website, s?.supplierURL].map(urlTextOf).find(Boolean);
  if (product && !isHomepage(product)) return { websiteUrl: product, websiteKind: 'product' };
  const fallback = site || product;
  return fallback ? { websiteUrl: fallback, websiteKind: 'supplier' } : {};
};

/** The card's words for that link. */
export const websiteLabelOf = (kind?: WebsiteKind): string =>
  kind === 'product' ? 'Product page' : 'Supplier website';
